import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Chip,
  Tooltip,
  alpha,
  Card,
  CardContent,
  Grid,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  DialogTitle,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ContainerWithCircularProgress from "../../../components/ContainerWithCircularProgress";
import ItemsList from "../../../components/ItemsList";
import UserDialog from "../../../components/Dialog/UserDialog";
import { UpgradesAndProfilesInfo } from "../../../model/Dto/UpgradesAndProfilesInfo";
import { WeaponProfile, WeaponUpgrade } from "../../../model/Types";
import { Api } from "../../../request/api/api";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import BuildIcon from "@mui/icons-material/Build";
import blue from "@mui/material/colors/blue";

interface UpgradesAndProfilesDialogProps {
  open: boolean;
  onClose: () => void;
  fighterWeaponId?: string;
  teamId: string;
  fighterId: string;
}

// Тип для табов
type DialogTab = 'profiles' | 'upgrades';

export default function UpgradesAndProfilesDialog({
  open,
  onClose,
  fighterWeaponId,
  teamId,
  fighterId
}: UpgradesAndProfilesDialogProps) {
  const [loading, setLoading] = React.useState(false);
  const [activeTab, setActiveTab] = useState<DialogTab>('profiles');

  const [upgradesAndProfiles, setUpgradesAndProfiles] = useState<
    UpgradesAndProfilesInfo | undefined
  >(undefined);
  const [cash, setCash] = useState<number | undefined>(undefined);

  const UpdateTeamCash = (teamId: string) => {
    setLoading(true);
    Api.team.getTeamCash(teamId).then((result) => {
      setCash(result.cash);
      setLoading(false);
    });
  };

  const fetchData = () => {
    if (fighterWeaponId) {
      setLoading(true);
      Api.fighterWeapon
        .getUpgradesAndProfiles(fighterId, fighterWeaponId)
        .then((result) => {
          setLoading(false);
          setUpgradesAndProfiles(result.upgradesAndProfilesInfo);
        });
    }
  };

  const addProfile = (id: string) => {
    if (fighterWeaponId) {
      setLoading(true);
      Api.fighterWeapon.addProfile(fighterId, fighterWeaponId, id, true).then((_) => {
        fetchData();
        UpdateTeamCash(teamId);
      });
    }
  };

  const removeProfile = (id: string) => {
    if (fighterWeaponId) {
      setLoading(true);
      Api.fighterWeapon
        .removeProfile(fighterId, fighterWeaponId, id, 1)
        .then((_) => {
          fetchData();
          UpdateTeamCash(teamId);
        });
    }
  };

  const addUpgrade = (id: string) => {
    if (fighterWeaponId) {
      setLoading(true);
      Api.fighterWeapon.addUpgrade(fighterId, fighterWeaponId, id, true).then((_) => {
        fetchData();
        UpdateTeamCash(teamId);
      });
    }
  };

  const removeUpgrade = (id: string) => {
    if (fighterWeaponId) {
      setLoading(true);
      Api.fighterWeapon
        .removeUpgrade(fighterId, fighterWeaponId, id, 1)
        .then((_) => {
          fetchData();
          UpdateTeamCash(teamId);
        });
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
      UpdateTeamCash(teamId);
    }
  }, [open, fighterWeaponId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: DialogTab) => {
    setActiveTab(newValue);
  };

  return (
    <UserDialog
      open={open}
      handleClose={onClose}
      maxWidth="lg"
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MilitaryTechIcon color="primary" />
            <Box>
              <Typography variant="h5" component="div" fontWeight={700}>
                Настройка оружия
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Измените профили и апгрейды вашего оружия
              </Typography>
            </Box>
          </Box>
          {loading === true ? (
            <CircularProgress
              size={24}
              sx={{
                color: blue[500],
                marginLeft: "12px",
              }}
            />
          ) : (
            <></>
          )}
          {cash !== undefined ? <Chip label={`${cash} credits`} /> : <></>}
        </Stack>
      </DialogTitle>

      {/* ТАБЫ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          aria-label="weapon customization tabs"
        >
          <Tab 
            label="Профили" 
            value="profiles"
            icon={<BuildIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Апгрейды" 
            value="upgrades"
            icon={<AddIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {!upgradesAndProfiles ? (
          <ContainerWithCircularProgress height="400px" />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {activeTab === 'profiles' ? (
              <ProfilesTab
                profiles={upgradesAndProfiles.profiles}
                onAddProfile={addProfile}
                onRemoveProfile={removeProfile}
              />
            ) : (
              <UpgradesTab
                upgrades={upgradesAndProfiles.upgrades}
                onAddUpgrade={addUpgrade}
                onRemoveUpgrade={removeUpgrade}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Done
        </Button>
      </DialogActions>
    </UserDialog>
  );
}

// КОМПОНЕНТ ДЛЯ ТАБА ПРОФИЛЕЙ
interface ProfilesTabProps {
  profiles: {
    mounted: WeaponProfile[];
    available: WeaponProfile[];
  };
  onAddProfile: (id: string) => void;
  onRemoveProfile: (id: string) => void;
}

function ProfilesTab({ profiles, onAddProfile, onRemoveProfile }: ProfilesTabProps) {
  return (
    <>
      {/* CURRENT CONFIGURATION - КОРОТКИЙ ФОРМАТ */}
      {profiles.mounted.length > 0 && (
        <Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'primary.main'
              }}
            >
              <BuildIcon /> Установленные профили
            </Typography>
            <Chip
              label={`${profiles.mounted.length} установлено`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <List dense>
              <ItemsList
                items={profiles.mounted}
                renderItem={(profile: WeaponProfile) => (
                  <ListItem
                    divider
                    sx={{
                      backgroundColor: profile.isDefault
                        ? (theme) => alpha(theme.palette.warning.main, 0.05)
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.action.hover
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight={600}>
                            {profile.name}
                          </Typography>
                          {profile.isDefault ? (
                            <Chip
                              label="Default"
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                          ) :
                            <Chip
                              label={`${profile.cost} pts`}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          }
                          {profile.traits.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              ({profile.traits.map(t => t.name).join(', ')})
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Range: {profile.sr ?? '-'}/{profile.lr ?? '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Str: {profile.str ?? '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            AP: {profile.ap ?? '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Dmg: {profile.d ?? '-'}
                          </Typography>
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <Tooltip title={profile.isDefault ? "Профиль по умолчанию не может быть удалён" : "Удалить профиль"}>
                        <span>
                          <IconButton
                            onClick={() => onRemoveProfile(profile.id)}
                            disabled={profile.isDefault}
                            size="small"
                            color="error"
                            edge="end"
                            sx={{
                              '&:hover': { backgroundColor: 'error.light' }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
              />
            </List>
          </Paper>
        </Box>
      )}

      {/* AVAILABLE PROFILES - КАРТОЧКИ */}
      {profiles.available.length > 0 && (
        <Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'success.main'
              }}
            >
              <AddIcon /> Доступны для покупки
            </Typography>
            <Chip
              label={`${profiles.available.length} доступно`}
              color="success"
              variant="outlined"
              size="small"
            />
          </Box>

          <Grid container spacing={2}>
            <ItemsList
              items={profiles.available}
              renderItem={(profile: WeaponProfile) => (
                <Grid item xs={12} sm={6} md={4}>
                  <AvailableProfileCard
                    profile={profile}
                    onAdd={() => onAddProfile(profile.id)}
                  />
                </Grid>
              )}
            />
          </Grid>
        </Box>
      )}
    </>
  );
}

// КОМПОНЕНТ ДЛЯ ТАБА АПГРЕЙДОВ
interface UpgradesTabProps {
  upgrades: {
    mounted: WeaponUpgrade[];
    available: WeaponUpgrade[];
  };
  onAddUpgrade: (id: string) => void;
  onRemoveUpgrade: (id: string) => void;
}

function UpgradesTab({ upgrades, onAddUpgrade, onRemoveUpgrade }: UpgradesTabProps) {
  return (
    <>
      {/* MOUNTED UPGRADES - КОРОТКИЙ ФОРМАТ */}
      {upgrades.mounted.length > 0 && (
        <Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'primary.main'
              }}
            >
              <BuildIcon /> Установленные апгрейды
            </Typography>
            <Chip
              label={`${upgrades.mounted.length} установлено`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <List dense>
              <ItemsList
                items={upgrades.mounted}
                renderItem={(upgrade: WeaponUpgrade) => (
                  <ListItem
                    divider
                    sx={{
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.action.hover
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight={600}>
                            {upgrade.name}
                          </Typography>
                          <Chip
                            label={`${upgrade.cost} кредитов`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      }
                      secondary={
                        upgrade.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {upgrade.description}
                          </Typography>
                        )
                      }
                    />

                    <ListItemSecondaryAction>
                      <Tooltip title="Remove upgrade">
                        <IconButton
                          onClick={() => onRemoveUpgrade(upgrade.id)}
                          size="small"
                          color="error"
                          edge="end"
                          sx={{
                            '&:hover': { backgroundColor: 'error.light' }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
              />
            </List>
          </Paper>
        </Box>
      )}

      {/* AVAILABLE UPGRADES - КАРТОЧКИ */}
      {upgrades.available.length > 0 && (
        <Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'success.main'
              }}
            >
              <AddIcon /> Доступные апгрейды
            </Typography>
            <Chip
              label={`${upgrades.available.length} доступно`}
              color="success"
              variant="outlined"
              size="small"
            />
          </Box>

          <Grid container spacing={2}>
            <ItemsList
              items={upgrades.available}
              renderItem={(upgrade: WeaponUpgrade) => (
                <Grid item xs={12} sm={6} md={4}>
                  <AvailableUpgradeCard
                    upgrade={upgrade}
                    onAdd={() => onAddUpgrade(upgrade.id)}
                  />
                </Grid>
              )}
            />
          </Grid>
        </Box>
      )}
    </>
  );
}

// КАРТОЧКА ДЛЯ AVAILABLE PROFILES
interface AvailableProfileCardProps {
  profile: WeaponProfile;
  onAdd: () => void;
}

function AvailableProfileCard({ profile, onAdd }: AvailableProfileCardProps) {
  const isDefault = profile.isDefault;

  const traitsString = profile.traits.length > 0 
    ? profile.traits.map(t => t.name).join(', ')
    : '-';

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: isDefault ? 'warning.main' : 'divider',
        borderWidth: isDefault ? 2 : 1,
        position: 'relative',
        '&:hover': {
          boxShadow: 4,
          borderColor: 'success.main',
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
              {profile.name}
              {isDefault && (
                <Chip
                  label="Default"
                  size="small"
                  color="warning"
                  sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Typography>
          </Box>

          <Chip
            label={`${profile.cost} pts`}
            color="primary"
            size="small"
            sx={{ fontWeight: 700, ml: 1 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ХАРАКТЕРИСТИКИ */}
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <StatItem label="Range" value={`${profile.sr ?? '-'}/${profile.lr ?? '-'}`} />
          </Grid>
          <Grid item xs={4}>
            <StatItem label="Accuracy" value={`${profile.sm ?? '-'}/${profile.lm ?? '-'}`} />
          </Grid>
          <Grid item xs={4}>
            <StatItem label="Strength" value={profile.str ?? '-'} highlight />
          </Grid>
          <Grid item xs={4}>
            <StatItem label="AP" value={profile.ap ?? '-'} />
          </Grid>
          <Grid item xs={4}>
            <StatItem label="Damage" value={profile.d ?? '-'} />
          </Grid>
          <Grid item xs={4}>
            <StatItem label="Ammo" value={profile.am ?? '-'} />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Traits
              </Typography>
              <Tooltip title={traitsString} arrow placement="top">
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'help',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {traitsString}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </CardContent>

      <Box sx={{
        p: 2,
        pt: 1,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.03)
      }}>
        <Button
          onClick={onAdd}
          variant="contained"
          color="success"
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          sx={{
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'success.dark',
              transform: 'scale(1.02)',
            },
            transition: 'transform 0.2s',
          }}
        >
          Add to Weapon
        </Button>
      </Box>
    </Card>
  );
}

// КАРТОЧКА ДЛЯ AVAILABLE UPGRADES
interface AvailableUpgradeCardProps {
  upgrade: WeaponUpgrade;
  onAdd: () => void;
}

function AvailableUpgradeCard({ upgrade, onAdd }: AvailableUpgradeCardProps) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: 'divider',
        position: 'relative',
        '&:hover': {
          boxShadow: 4,
          borderColor: 'success.main',
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
              {upgrade.name}
            </Typography>
          </Box>

          <Chip
            label={`${upgrade.cost} pts`}
            color="primary"
            size="small"
            sx={{ fontWeight: 700, ml: 1 }}
          />
        </Box>

        {upgrade.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}
            >
              {upgrade.description}
            </Typography>
          </>
        )}
      </CardContent>

      <Box sx={{
        p: 2,
        pt: 1,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.03)
      }}>
        <Button
          onClick={onAdd}
          variant="contained"
          color="success"
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          sx={{
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'success.dark',
              transform: 'scale(1.02)',
            },
            transition: 'transform 0.2s',
          }}
        >
          Установить апгрейд
        </Button>
      </Box>
    </Card>
  );
}

// КОМПОНЕНТ ДЛЯ ОТОБРАЖЕНИЯ СТАТИСТИК
interface StatItemProps {
  label: string;
  value: string | number;
  highlight?: boolean | 'error';
}

function StatItem({ label, value, highlight }: StatItemProps) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: highlight === true ? 'primary.main' :
            highlight === 'error' ? 'error.main' : 'text.primary',
          fontSize: highlight ? '0.95rem' : '0.9rem'
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}